import React, { useState } from 'react';
import { Box, Paper, Typography, useTheme, Grid, LinearProgress } from "@mui/material";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { EmptyState } from '../../../../components/global';

const ExpenseTimeline = () => {
  const theme = useTheme();
  const { data, error, isLoading } = useSWR('/reports/expense-timeline', async () => {
    const response = await api.get('/reports/expense-timeline');
    return response;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Χρονοδιάγραμμα Εξόδων" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const timeline = data || {};
  const monthly = Array.isArray(timeline.monthly) ? timeline.monthly : [];
  const forecast = Array.isArray(timeline.forecast) ? timeline.forecast : [];

  // Format month labels for better display
  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαι', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const lineData = [
    {
      id: 'Πραγματικά',
      data: monthly.map((m) => ({ x: formatMonthLabel(m.month), y: m.actual || 0 })),
    },
    {
      id: 'Προβλέψεις',
      data: monthly.map((m) => ({ x: formatMonthLabel(m.month), y: m.predicted || 0 })),
    },
    ...(forecast.length > 0 ? [{
      id: 'Πρόβλεψη',
      data: forecast.map((f) => ({ x: formatMonthLabel(f.month), y: f.forecasted || 0 })),
    }] : []),
  ];

  const barData = monthly.map((m) => ({
    month: formatMonthLabel(m.month),
    actual: m.actual || 0,
    predicted: m.predicted || 0,
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Χρονοδιάγραμμα Εξόδων & Πρόβλεψη" module="Αναφορές" subtitle="Ανάλυση εξόδων ανά χρόνο" />

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 450, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Έξοδα ανά Μήνα
            </Typography>
            {monthly.length > 0 ? (
              <Box sx={{ height: 380 }}>
                <ResponsiveLine
                  data={lineData}
                  margin={{ top: 20, right: 80, bottom: 50, left: 80 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                  curve="monotoneX"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickRotation: -45,
                  }}
                  axisLeft={{
                    format: (value) => formatCurrency(value),
                  }}
                  pointSize={8}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  colors={[theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main]}
                  lineWidth={3}
                  theme={{
                    text: {
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    },
                    grid: {
                      line: {
                        stroke: theme.palette.divider,
                      },
                    },
                  }}
                  legends={[
                    {
                      anchor: 'top-right',
                      direction: 'column',
                      justify: false,
                      translateX: 100,
                      translateY: 0,
                      itemsSpacing: 0,
                      itemDirection: 'left-to-right',
                      itemWidth: 80,
                      itemHeight: 20,
                      symbolSize: 12,
                      symbolShape: 'circle',
                    },
                  ]}
                />
              </Box>
            ) : (
              <EmptyState title="Δεν υπάρχουν δεδομένα" />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 450, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Πραγματικά vs Προβλέψεις (Μηνιαία)
            </Typography>
            {barData.length > 0 ? (
              <Box sx={{ height: 380 }}>
                <ResponsiveBar
                  data={barData}
                  keys={['actual', 'predicted']}
                  indexBy="month"
                  margin={{ top: 20, right: 80, bottom: 50, left: 80 }}
                  padding={0.3}
                  valueFormat={formatCurrency}
                  colors={[theme.palette.info.main, theme.palette.warning.main]}
                  axisLeft={{
                    format: (value) => formatCurrency(value),
                  }}
                  axisBottom={{
                    tickRotation: -45,
                  }}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'top-right',
                      direction: 'column',
                      translateX: 100,
                      itemWidth: 80,
                      itemHeight: 20,
                    },
                  ]}
                  theme={{
                    text: {
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    },
                    grid: {
                      line: {
                        stroke: theme.palette.divider,
                      },
                    },
                  }}
                />
              </Box>
            ) : (
              <EmptyState title="Δεν υπάρχουν δεδομένα" />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExpenseTimeline;

